import React from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { J1DownarrowD } from '@e3dc-react/icons'
import { InactiveAi360LabelIcon } from 'src/Elements/LiveView/AlternativeView/InactiveAi360LabelIcon'
import { endOfHour, formatISO, startOfHour } from '@e3dc-react/shell/Libs/date'
import { useDynamicEms, useDynamicEmsData, useIsDynamicEmsAccessible } from 'src/Libs/dynamic-power-prices'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

const useStyles = makeStyles()(theme => ({
  accordion: {
    paddingBottom: 0,
    '&.Mui-expanded': {
      margin: 0,
    },
    '&:before': {
      display: 'none',
    },
  },
  accordionContent: {
    paddingInline: 0,
  },
  accordionSummary: {
    minHeight: 40,
    padding: 0,
    '&.Mui-expanded': {
      minHeight: 40,
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
  },
}))

const AI360AccordionForLiveView: React.FC = () => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const navigate = useNavigate()
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const { data: dynamicEmsSettings } = useDynamicEms(installationId, !!installationId)
  const { data: dynamicEmsData } = useDynamicEmsData(
    installationId,
    formatISO(startOfHour(new Date())),
    formatISO(endOfHour(new Date())),
    !!installationId && !!dynamicEmsSettings?.isActive && isDynamicEmsV2Accessible
  )
  const isDynamicEmsDataAvailableForBattery = !!dynamicEmsData?.at(-1)?.emsBatteryMode

  if (!isDynamicEmsV2Accessible || !isDynamicEmsDataAvailableForBattery) return null

  const handleClickMoreInfo = (): void => {
    navigate(`/dashboard/ai360/${installationId}/${storageId}`)
  }

  return (
    <>
      <Divider
        sx={{
          borderColor: 'grey.grey06',
          marginLeft: -4,
          marginRight: -4,
        }}
      />
      <Accordion classes={{ root: classes.accordion }} elevation={0}>
        <AccordionSummary expandIcon={<J1DownarrowD sx={{ color: 'grey.grey00' }} />} classes={{ root: classes.accordionSummary }}>
          <Stack flexDirection="row" alignItems="center" gap={1} marginLeft={-2}>
            <InactiveAi360LabelIcon height={18} style={{ color: theme.palette.primary.main }} />
            <Typography variant="h3">{t(`dashboard.live-view.ai360.status.${dynamicEmsData.at(-1)!.emsBatteryMode}`)}</Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails classes={{ root: classes.accordionContent }}>
          <Stack>
            <Typography variant="caption">{t(`dashboard.live-view.ai360.status.description.${dynamicEmsData.at(-1)!.emsBatteryMode}`)}</Typography>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="text" sx={{ pb: 0, minHeight: 0 }} onClick={handleClickMoreInfo}>
                {t('dashboard.live-view.ai360.more-info')}
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default AI360AccordionForLiveView
